import { useCallback, useEffect, useMemo, useState } from "react";
import shopApi from "../../../api/shopApi";
import { useContacts } from "../contexts/ContactsContext";

/**
 * Hook load gợi ý kết bạn từ Zalo.
 * zca-js `api.getFriendRecommendations()` trả full list một lần — không paginated.
 * Load full 1 lần khi shop đổi, slice ở client để "Xem thêm" 20 mỗi click.
 *
 * Hỗ trợ multi-shop: gọi parallel cho từng shop, gộp lại (dedup theo userId).
 */
export const useFriendSuggestions = ({ pageSize = 20 } = {}) => {
  const { selectedShopIds, shops, search } = useContacts();
  const [rawItems, setRawItems] = useState([]);
  const [visible, setVisible] = useState(pageSize);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!selectedShopIds.length) {
      setRawItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.all(
        selectedShopIds.map(async (shopId) => {
          try {
            const res = await shopApi.getFriendSuggestions(shopId);
            const list = Array.isArray(res?.data) ? res.data : [];
            const shop = shops.find((s) => s._id === shopId);
            return list.map((it) => ({
              ...it,
              shopId,
              shopName: shop?.name,
            }));
          } catch (e) {
            console.error(`[useFriendSuggestions] shop ${shopId} error:`, e);
            return [];
          }
        }),
      );

      // Dedup theo (shopId, userId) — cùng user nhưng khác shop vẫn là row riêng
      const seen = new Set();
      const merged = results.flat().filter((r) => {
        const key = `${r.shopId}:${r.userId}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setRawItems(merged);
      setVisible(pageSize);
    } catch (e) {
      setError(e.message || "Lỗi tải danh sách");
    } finally {
      setLoading(false);
    }
  }, [selectedShopIds, shops, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  // Search filter theo tên + SĐT
  const filtered = useMemo(() => {
    if (!search || !search.trim()) return rawItems;
    const q = search.trim().toLowerCase();
    return rawItems.filter((r) => {
      const name = (r.displayName || r.zaloName || "").toLowerCase();
      const phone = r.phoneNumber || "";
      return name.includes(q) || String(phone).includes(q);
    });
  }, [rawItems, search]);

  const items = useMemo(() => filtered.slice(0, visible), [filtered, visible]);
  const hasMore = filtered.length > visible;
  const total = filtered.length;

  const showMore = useCallback(() => {
    setVisible((v) => v + pageSize);
  }, [pageSize]);

  const removeById = useCallback((userId, shopId) => {
    setRawItems((prev) =>
      prev.filter((r) => !(r.userId === userId && r.shopId === shopId)),
    );
  }, []);

  return {
    items,
    total,
    loading,
    error,
    hasMore,
    showMore,
    reload: load,
    removeById,
  };
};

export default useFriendSuggestions;
